/**
 * nav-search — ONE vocabulary for both search surfaces.
 *
 * 2026-09-11 (KP): "When I say location or store, all these words mean campus.
 * If I cannot find it, then how will the end user be able to fix it?"
 *
 * The Locations CRUD (list + editor + bulk import, route `locations`) has
 * always existed, but on a university tenant the rail entry is labelled
 * "Campuses" — the tenant noun, served through the nav composition — so a
 * search for "location" matched nothing. The same feature is "Branches" at
 * a bank tenant and "Boutiques" at a retail tenant. A tenant renaming a surface must
 * never make it unfindable by its own product name, and vice versa.
 *
 * So a feature is searchable by FOUR things, not one:
 *   1. its rendered label (what the user sees, in their language),
 *   2. its catalog id ("ab-branches"),
 *   3. its route ("locations", "locations.edit"),
 *   4. the aliases below — product name, tenant nouns, and the plain words
 *      people actually type.
 *
 * Aliases are additive and cheap. When a surface gains a tenant noun, add the
 * noun here; do not rename anything to suit search.
 */

/** Alias sets, keyed by rail catalog id. Lowercase, singular AND plural — the
 *  match is a substring test, so "campus" already covers "campuses", but the
 *  reverse is not true for irregulars, and typing the plural is common. */
const NAV_ALIASES: Record<string, string[]> = {
  /* The one that started this: every tenant noun for the same CRUD. */
  locations: ["location", "locations", "campus", "campuses", "branch", "branches", "store", "stores", "boutique", "boutiques", "venue", "venues", "site", "sites", "address", "addresses", "opening hours", "time zone"],
  "ab-branches": ["location", "locations", "campus", "campuses", "branch", "branches", "store", "stores", "boutique", "boutiques", "venue", "venues", "site", "sites", "address", "addresses", "opening hours", "time zone"],
  nodes: ["organisation", "organization", "org structure", "node", "nodes", "tree", "hierarchy", "department", "departments", "division"],
  people: ["staff", "people", "team", "member", "members", "roles", "employee", "employees", "advisor", "advisors", "availability", "absence", "absences"],
  staff: ["staff", "people", "team", "roster", "employee", "employees"],
  roles: ["role", "roles", "permission", "permissions", "access", "capability", "capabilities"],
  resources: ["resource", "resources", "room", "rooms", "desk", "desks", "equipment", "asset", "assets"],
  customers: ["customer", "customers", "student", "students", "client", "clients", "guest", "guests", "contact", "contacts", "directory"],
  guests: ["guest", "guests", "attendee", "attendees", "check-in", "checkin", "registration", "registrations"],
  "ev-section": ["event", "events", "seminar", "seminars", "fair", "fairs", "workshop", "workshops"],
  "appt-book": ["appointment", "appointments", "booking", "bookings", "diary", "schedule"],
  "q-board": ["queue", "queues", "walk-in", "walkin", "walk in", "lobby", "line"],
  "q-dash": ["queue", "queues", "dashboard", "wait time", "sla"],
  "cs-questions": ["question", "questions", "booking questions", "form", "forms", "field", "fields"],
  promos: ["coupon", "coupons", "promo", "promos", "promotion", "promotions", "discount", "discounts", "code", "codes"],
  surveys: ["survey", "surveys", "feedback", "nps", "questionnaire"],
  services: ["service", "services", "catalogue", "catalog", "offering", "offerings", "duration"],
  "appt-catalogue": ["service", "services", "catalogue", "catalog", "appointment services"],
  journeys: ["journey", "journeys", "flow", "flows", "funnel"],
  "new-journeys": ["journey builder", "journey", "journeys", "designer", "builder", "flow", "flows"],
  "all-journeys": ["all journeys", "journey", "journeys", "appointment page", "booking page", "kiosk", "walk-in", "event list", "event landing"],
  "manage-pages": ["confirmation", "confirmation page", "confirmation pages", "manage pages", "page", "pages", "booking page"],
  links: ["landing", "landing page", "landing pages", "link", "links", "public page"],
  branding: ["brand", "branding", "logo", "colour", "color", "theme", "identity"],
  reports: ["report", "reports", "analytics", "insight", "insights", "metric", "metrics", "performance"],
  orders: ["order", "orders", "payment", "payments", "invoice", "invoices", "refund", "refunds"],
  settings: ["setting", "settings", "configuration", "config", "preferences", "admin"],
  messaging: ["message", "messages", "messaging", "inbox", "sms", "email", "notification", "notifications"],
  campaigns: ["campaign", "campaigns", "email", "sms", "send", "sends", "broadcast"],
  approvals: ["approval", "approvals", "review", "sign off", "sign-off", "pending"],
  "wf-section": ["workforce", "staffing", "rota", "shift", "shifts", "schedule", "scheduling"],
  "wf-staffing": ["staffing", "shift", "shifts", "event staffing", "roles per event"],
  "floor-calendar": ["floor", "calendar", "floor calendar", "day view"],
  "region-calendar": ["region", "calendar", "regional"],
  "so-floor": ["store ops", "storeops", "floor", "shop floor"],
  "ab-today": ["today", "now", "live", "pulse", "dashboard", "home"],
  "ab-audit": ["audit", "audit log", "history", "activity", "trail"],
  calendar: ["calendar", "week", "month", "agenda", "diary"],
};

/** Everything a feature can be found by, lowercased. */
export function navSearchTerms(item: { id: string; label: string; labelKey?: string }, route?: string): string[] {
  const out = [item.label, item.id, route || ""].filter(Boolean).map((s) => s.toLowerCase());
  (NAV_ALIASES[item.id] || []).forEach((a) => out.push(a));
  /* Route-keyed aliases too, so a served item with an unfamiliar id still
     inherits the vocabulary of the surface it opens (ab-branches -> locations). */
  if (route) (NAV_ALIASES[route.split(".")[0]] || []).forEach((a) => out.push(a));
  return out;
}

/** Substring match across label, id, route and aliases. `query` pre-lowercased
 *  by the caller is fine; this lowercases defensively. */
export function matchesNavQuery(item: { id: string; label: string; labelKey?: string }, query: string, route?: string): boolean {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  return navSearchTerms(item, route).some((term) => term.includes(q));
}
